import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import TimeAgo from 'react-timeago';
import moment from 'moment';
import { CircularProgress, Tooltip, Typography, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { InfoOutlined, ArrowRight, MonetizationOnOutlined } from '@material-ui/icons';
import { convertDateToLocal, convertNotesDateToPST } from '../../../helpers/audit-trail';
import GradientBar from '../components/BarChart';
import ticketImage from '../../../images/sales-stream/sales-stream-ticket.svg';
import {
  checkIfTargetAvailable,
  formatLastPriceChangeDate,
  formatLastSaleDate,
  hasCotaAtFloorAccess,
} from '../../../helpers/sales-stream';
import CountdownTimer from '../components/CountDownTimer';
import SSMoney from '../../../images/sales-stream/sales-stream-money.svg';
import SSCrying from '../../../images/sales-stream/sales-stream-crying.svg';
import useWindowSize from '../../../hooks/useWindowsSize';

const useStyles = makeStyles({
  eventContent: {
    transition: 'opacity 0.3s ease-out, transform 0.3s ease-out', // Smooth transition
  },
  content: {
    padding: '10px 10px',
    margin: '5px 5px 10px 5px',
    backgroundColor: '#3E414B',
    minHeight: '100px',
    maxHeight: '110px',
    border: '2px solid #3E414B',
    borderRadius: '13px',
    color: '#FFFFFF',
    width: '105%',
  },
  contentHigher: {
    maxHeight: '135px !important',
  },
  space: {
    marginLeft: '4px',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
  },
  eventInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
  },
  barGraph: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: '5px',
    paddingLeft: '20px',
  },
  eventStats: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    whiteSpace: 'nowrap',
    fontSize: '14px',
  },
  boldText: {
    fontWeight: 'bold',
    marginLeft: '10px',
  },
  normalText: {
    fontWeight: 'normal',
  },
  nameLink: {
    color: '#FFFFFF',
    '&:hover': {
      color: '#D784FF',
    },
  },
  gradientDiv: {
    margin: '10px 5px 0px 5px',
    borderRadius: '20px',
    padding: '10px',
    width: '40px',
    height: '60px',
    background: 'linear-gradient(45deg, #A91CED, #FF2C6C)', // 45-degree angle gradient
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  gradientDivHourMin: {
    height: '80px',
  },
  gradientDivLineOnly: {
    paddingTop: '5px',
    width: '1px',
    height: '100%',
    background: '#A91CED',
  },
  timeIndicatorTime: {
    textAlign: 'center',
    fontSize: '12px',
  },
  timeIndicatorNumber: {
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  timeIndicatorHourMin: {
    textAlign: 'center',
    fontSize: '13px',
    fontWeight: 'bold',
  },
  salesStreamTooltip: {
    backgroundColor: '#1F2128',
    opacity: '0.8 !important',
    top: '-20px',
    left: '-50px',
    position: 'absolute',
    minWidth: '200px',
    textAlign: 'center',
  },
  spanTooltip: {
    fontSize: '10px',
  },
  cotaTooltip: {
    backgroundColor: '#1F2128',
    opacity: '0.8 !important',
  },
  marginSpanTooltip: {
    fontSize: '12px',
  },
  marginTooltip: {
    background: 'linear-gradient(to right, #00CFFFBD, #0783FEBD)',
    fontSize: '20px',
    color: '#fff',
    maxWidth: '150px',
    height: 'auto',
    borderRadius: '4px',
  },
  marginArrow: {
    color: 'transparent',
    '&::before': {
      background: '#00CFFFBD',
    },
  },
  spanSellOutTooltip: {
    fontSize: '11px',
  },
  sellOutTooltip: {
    backgroundColor: '#1F2128',
    opacity: '0.8 !important',
    display: 'flex',
    flexDirection: 'flex-start',
    margin: '2px',
  },
  soldQty: {
    fontSize: '10px',
    fontWeight: 400,
    lineHeight: '12.1px',
    color: '#FFFFFF',
  },
  cotaAtFloorTooltip: {
    background: 'linear-gradient(90deg, #00CFFF 0%, #0783FE 121.03%)',
    borderRadius: '0px',
    fontSize: '0.875rem',
    margin: '6px 0px',
    padding: '1px 6px 0px',
  },
  cotaAtFlootTooltipArrow: {
    color: '#089aff',
    transform: 'translate(-13px, 4px)',
  },
  cotaAtFloorArrowIcon: {
    height: '1rem',
    width: '1rem',
  },
  dollarIcon: {
    height: '1.25rem',
    width: '1.25rem',
  },
  cotaInfoContainer: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: '4px',
  },
  cotaSummary: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: '1px',
  },
});

const SalesStreamContentPanel = ({
  eventId,
  name,
  cota,
  isFirst,
  datetime,
  beforeEvent,
  isFetching,
  heldQuantity,
  heldPercentage,
  target,
  targetPercentage,
  showTargetHeld,
  currHourSales,
  pastHourSales,
  lastDaySales,
  lastDayAllCost,
  lastDayAllSales,
  sellOutHour,
  lastPriceChange,
  lastUserChange,
  lastSoldPrice,
  lastSoldCost,
  isFeatureAvailableForOrg,
  isGradientVisible,
  lastSaleDate,
  lastSoldMarketplace,
  isPanelOpen,
  cotaAtFloorPercentage,
}) => {
  const classes = useStyles();
  const SellOutdate = new Date(convertNotesDateToPST(sellOutHour));
  const [profit, setProfit] = useState(0);
  const [lastPriceChangeTime, setLastPriceChangeTime] = useState();
  const { width } = useWindowSize();
  const [screenSize, setScreenSize] = useState('large-open');
  const showCotaAtFloorPercentageTooltip = hasCotaAtFloorAccess();

  useEffect(() => {
    let newSize;
    if (width >= 1280 && width < 1500) {
      newSize = 'sm';
    } else if (width >= 1500 && width < 1650) {
      newSize = 'md';
    } else if (width >= 1650 && width < 1700) {
      newSize = 'lg';
    } else if (width >= 1700) {
      newSize = 'xl';
    } else {
      newSize = 'large';
    }
    if (isPanelOpen) {
      newSize += '-open';
    }

    if (newSize) {
      setScreenSize(newSize);
    }
  }, [width, isPanelOpen]);

  const formattedTime = SellOutdate.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  if (isFetching) {
    return (
      <div>
        <Grid container>
          <Grid item xs={6} />
          <Grid item xs={6}>
            <CircularProgress size={36} className={classes.buttonProgress} />
          </Grid>
        </Grid>
      </div>
    );
  }

  const timeGradient = () => {
    const timeBefore = String(beforeEvent / 60);
    if (timeBefore.includes('.')) {
      const timeSplit = timeBefore.split('.');
      const hour = timeSplit[0];
      const minutes = parseInt(timeSplit[1], 10) * 6;
      if (hour === '0' || hour === '-0') {
        return (
          <div className={` ${classes.gradientDiv} ${classes.gradientDiv}`}>
            <span className={classes.timeIndicatorNumber}>{hour === '-0' ? `-${minutes}` : minutes}</span>
            <span className={classes.timeIndicatorTime}>MIN</span>
          </div>
        );
      }
      return (
        <div className={` ${classes.gradientDiv} ${classes.gradientDivHourMin}`}>
          <span className={classes.timeIndicatorNumber}>{hour}</span>
          <span className={classes.timeIndicatorTime}>HR</span>
          <span className={classes.timeIndicatorNumber}>{minutes}</span>
          <span className={classes.timeIndicatorTime}>MIN</span>
        </div>
      );
    }
    if (timeBefore === '0') {
      return (
        <div className={classes.gradientDiv}>
          <span className={classes.timeIndicatorNumber}>{timeBefore}</span>
          <span className={classes.timeIndicatorTime}>MIN</span>
        </div>
      );
    }
    return (
      <div className={classes.gradientDiv}>
        <span className={classes.timeIndicatorNumber}>{timeBefore}</span>
        <span className={classes.timeIndicatorTime}>HR</span>
      </div>
    );
  };

  // Calculate Profit Margin to be displayed in the UI
  const calculateMargin = () => {
    if (lastDayAllSales && lastDayAllSales === 0) {
      return `Margin ---`;
    }
    if (lastDayAllSales && lastDayAllCost) {
      const margin = ((lastDayAllSales - lastDayAllCost) / lastDayAllSales) * 100;
      return `Margin ${margin.toFixed(2)}%`;
    }
    return `Margin ---`;
  };

  // Custom formatter function
  const customFormatter = (value, unit, suffix, date) => {
    const current = new Date();
    const now = moment(current);
    const eventTime = moment(date);
    const duration = moment.duration(eventTime.diff(now));
    const hours = Math.floor(duration.asHours());
    const minutes = Math.floor(duration.asMinutes()) % 60;

    let formatted = '';

    if (hours > 0) {
      formatted += `${hours} hour${hours > 1 ? 's' : ''}`;
      if (minutes > 0) {
        formatted += ` and ${minutes} minute${minutes > 1 ? 's' : ''}`;
      }
    } else if (minutes >= 0) {
      formatted += `${minutes} minute${minutes > 1 ? 's' : ''}`;
    } else {
      const durationHigher = moment.duration(now.diff(eventTime));
      const durationHigherMins = durationHigher.asMinutes();
      const minutesHigher = Math.floor(durationHigher.asMinutes()) % 60;
      if (durationHigherMins >= 60) {
        formatted += `1 hour and ${minutesHigher} minute${minutesHigher > 1 ? 's' : ''}`;
      } else {
        formatted += `${minutesHigher} minute${minutesHigher > 1 ? 's' : ''}`;
      }
      formatted += ' After Event';
      return formatted;
    }

    formatted += ' Before Event';

    return formatted;
  };

  const timeBeforeEvent = () => {
    const date = convertDateToLocal(datetime);
    return <TimeAgo date={date} formatter={customFormatter} />;
  };

  useEffect(() => {
    if (lastPriceChange) {
      const eventLastPriceChange = lastPriceChange;
      setLastPriceChangeTime(eventLastPriceChange);
    }
  }, [lastPriceChange]);

  useEffect(() => {
    setProfit(lastSoldPrice - lastSoldCost);
  }, [lastSoldCost, lastSoldPrice]);

  return (
    <Tooltip
      classes={{ tooltip: classes.salesStreamTooltip }}
      placement="top"
      title={<span className={classes.spanTooltip}>{timeBeforeEvent()}</span>}
    >
      <div className={classes.eventContent}>
        <Grid container>
          {isGradientVisible && (
            <Grid item xs={1} style={{ maxWidth: '3.5%', display: 'flex', justifyContent: 'center' }}>
              {isFirst ? (
                <Grid container>
                  <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center' }}>
                    {timeGradient()}
                  </Grid>
                  <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center' }}>
                    <div className={classes.gradientDivLineOnly} />
                  </Grid>
                </Grid>
              ) : (
                <div className={classes.gradientDivLineOnly} />
              )}
            </Grid>
          )}
          <Grid item xs={11}>
            <div className={name.length > 45 ? `${classes.content} ${classes.contentHigher}` : classes.content}>
              <Grid container alignItems="flex-start">
                <Grid item xs={2} className={classes.eventInfo}>
                  <Grid container>
                    <Grid item xs={12}>
                      <Grid container>
                        <Grid item xs={12}>
                          <a
                            href={`/event/pricing/${eventId}`}
                            className={classes.nameLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {name}
                          </a>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} style={{ paddingTop: '15px', paddingLeft: '10px' }}>
                      <Grid container>
                        {lastSaleDate && lastSoldMarketplace && (
                          <Grid item xs={3}>
                            <img src={profit >= 0 ? SSMoney : SSCrying} alt="" style={{ height: '35px' }} />
                          </Grid>
                        )}
                        <Grid item xs={9}>
                          <Grid container>
                            <Grid item xs={12}>
                              Last Sale:
                            </Grid>
                            <Grid item xs={12}>
                              {formatLastSaleDate(lastSaleDate, lastSoldMarketplace)}
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid
                  item
                  xs={screenSize === 'md' ? 8 : 7}
                  lg={screenSize === 'lg-open' ? 7 : 8}
                  xl={8}
                  className={classes.barGraph}
                  style={{
                    paddingRight:
                      screenSize === 'sm' || screenSize === 'md-open' || screenSize === 'lg-open' ? '0px' : '20px',
                  }}
                >
                  <Grid container alignItems="flex-start" style={{ width: '100%' }}>
                    <Grid item xs={12}>
                      <Grid
                        container
                        direction="row"
                        style={{
                          width: `${Math.min(heldPercentage, 96)}%`,
                          height: '100%',
                          minWidth: '75px',
                          paddingRight: '15px',
                        }}
                        justifyContent="flex-end"
                      >
                        <Grid item>
                          <Typography style={{ fontSize: '10px', fontWeight: 400, lineHeight: '12.1px' }}>
                            Actual
                          </Typography>
                        </Grid>
                        <Grid item style={{ padding: '0px 3px', marginTop: '-2px' }}>
                          <img alt="tickets" src={ticketImage} style={{ width: '11px', height: '7px' }} />
                        </Grid>
                        <Grid item>
                          <Typography style={{ fontSize: '10px', fontWeight: 400, lineHeight: '12.1px' }}>
                            {heldQuantity}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item style={{ width: '100%', height: '100%' }}>
                      <Grid container>
                        <Grid item xs={12}>
                          <GradientBar
                            eventId={eventId}
                            heldQuantity={heldQuantity}
                            percentage={heldPercentage}
                            showTarget={showTargetHeld}
                            target={target}
                            targetPercentage={targetPercentage}
                          />
                        </Grid>
                        <Grid item xs={12} style={{ margin: '10px' }}>
                          <Grid container direction="row" style={{ justifyContent: 'center' }}>
                            {checkIfTargetAvailable() && isFeatureAvailableForOrg ? (
                              <>
                                <Grid item style={{ padding: '0px 3px 0px 7px', marginTop: '-3px' }}>
                                  <img alt="tickets" src={ticketImage} style={{ width: '11px', height: '7px' }} />
                                </Grid>
                                <Grid item>
                                  <Typography className={classes.soldQty}>
                                    {`${Math.max(heldQuantity - target, 0) ??
                                      0} Current Hour Target Sell Qty \xa0 - \xa0`}
                                  </Typography>
                                </Grid>
                              </>
                            ) : (
                              <Grid item>
                                <Typography className={classes.soldQty}>{`- \xa0`}</Typography>
                              </Grid>
                            )}
                            <Grid item style={{ padding: '0px 3px 0px 7px', marginTop: '-3px' }}>
                              <img alt="tickets" src={ticketImage} style={{ width: '11px', height: '7px' }} />
                            </Grid>
                            <Grid item>
                              <Typography className={classes.soldQty}>
                                {`${currHourSales} Sold Current Hour \xa0 - \xa0`}
                              </Typography>
                            </Grid>
                            <Grid item style={{ padding: '0px 3px', marginTop: '-3px' }}>
                              <img alt="tickets" src={ticketImage} style={{ width: '11px', height: '7px' }} />
                            </Grid>
                            <Grid item>
                              <Typography className={classes.soldQty}>
                                {`${pastHourSales} Sold Last Hour \xa0 -`}
                              </Typography>
                            </Grid>
                            <Grid item style={{ padding: '0px 3px', marginTop: '-3px' }}>
                              <img alt="tickets" src={ticketImage} style={{ width: '11px', height: '7px' }} />
                            </Grid>
                            <Grid item>
                              <Typography className={classes.soldQty}>
                                {`${lastDaySales} Sold Yesterday \xa0`}
                              </Typography>
                            </Grid>
                            <Grid item>
                              <Tooltip
                                placement="right"
                                classes={{ arrow: classes.marginArrow, tooltip: classes.marginTooltip }}
                                title={<span className={classes.marginSpanTooltip}>{calculateMargin()}</span>}
                                arrow
                              >
                                <InfoOutlined style={{ color: '#449DFF', fontSize: 18, marginTop: '-2px' }} />
                              </Tooltip>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid
                  item
                  xs={screenSize === 'md' ? 2 : 3}
                  lg={screenSize === 'lg-open' ? 3 : 2}
                  xl={2}
                  className={classes.eventStats}
                  style={{
                    paddingLeft:
                      screenSize === 'sm' || screenSize === 'md-open' || screenSize === 'lg-open' ? '4%' : '',
                  }}
                >
                  <Grid container>
                    {checkIfTargetAvailable() && sellOutHour && isFeatureAvailableForOrg && (
                      <Tooltip
                        placement="top-start"
                        classes={{ tooltip: classes.sellOutTooltip }}
                        title={
                          <span className={classes.spanSellOutTooltip}>{`${formattedTime} Target Sell Out Hour`}</span>
                        }
                      >
                        <Grid item xs={showCotaAtFloorPercentageTooltip ? 7 : 8}>
                          <CountdownTimer targetDateTime={sellOutHour} />
                        </Grid>
                      </Tooltip>
                    )}
                    <Grid item xs={showCotaAtFloorPercentageTooltip ? 5 : 4} className={classes.cotaSummary}>
                      <Tooltip
                        placement="top"
                        classes={{ tooltip: classes.cotaTooltip }}
                        title={<span className={classes.spanTooltip}>COTA</span>}
                      >
                        <Box className={classes.cotaInfoContainer}>
                          <MonetizationOnOutlined className={classes.dollarIcon} />
                          <span>{Math.round(cota).toLocaleString()}</span>
                        </Box>
                      </Tooltip>
                      {showCotaAtFloorPercentageTooltip ? (
                        <Tooltip
                          classes={{
                            tooltip: classes.cotaAtFloorTooltip,
                            arrow: classes.cotaAtFlootTooltipArrow,
                          }}
                          placement="bottom-end"
                          title={`${cotaAtFloorPercentage}% of COTA priced at floor`}
                          arrow
                        >
                          <Box display="flex" alignItems="center" color="#FD4962">
                            <ArrowRight className={classes.cotaAtFloorArrowIcon} />
                            <Typography variant="body2">{`${cotaAtFloorPercentage}%`}</Typography>
                          </Box>
                        </Tooltip>
                      ) : null}
                    </Grid>
                    {lastPriceChangeTime && (
                      <Grid container style={{ paddingTop: '5px' }}>
                        <Grid item xs={12}>
                          {`Last Price Change: `}
                          <span style={{ fontWeight: 'bold' }}>{lastUserChange}</span>
                        </Grid>
                        <Grid item xs={12}>
                          {formatLastPriceChangeDate(lastPriceChangeTime)}
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </div>
          </Grid>
        </Grid>
      </div>
    </Tooltip>
  );
};

SalesStreamContentPanel.defaultProps = {
  eventId: undefined,
  name: undefined,
  cota: undefined,
  isFirst: undefined,
  datetime: undefined,
  beforeEvent: undefined,
  isFetching: undefined,
  heldQuantity: undefined,
  heldPercentage: undefined,
  target: undefined,
  targetPercentage: undefined,
  showTargetHeld: false,
  currHourSales: 0,
  pastHourSales: 0,
  lastDaySales: 0,
  lastDayAllCost: 0,
  lastDayAllSales: 0,
  lastSoldPrice: 0,
  lastSoldCost: 0,
  sellOutHour: undefined,
  lastPriceChange: undefined,
  lastUserChange: '',
  lastSoldMarketplace: undefined,
  lastSaleDate: undefined,
  isFeatureAvailableForOrg: true,
  isGradientVisible: true,
  isPanelOpen: true,
  cotaAtFloorPercentage: 0,
};

SalesStreamContentPanel.propTypes = {
  eventId: PropTypes.number,
  name: PropTypes.string,
  cota: PropTypes.number,
  isFirst: PropTypes.bool,
  datetime: PropTypes.string,
  beforeEvent: PropTypes.number,
  isFetching: PropTypes.bool,
  heldQuantity: PropTypes.number,
  heldPercentage: PropTypes.number,
  target: PropTypes.number,
  targetPercentage: PropTypes.number,
  showTargetHeld: PropTypes.bool,
  currHourSales: PropTypes.number,
  pastHourSales: PropTypes.number,
  lastDaySales: PropTypes.number,
  lastDayAllCost: PropTypes.number,
  lastDayAllSales: PropTypes.number,
  sellOutHour: PropTypes.string,
  lastPriceChange: PropTypes.string,
  lastUserChange: PropTypes.string,
  lastSoldMarketplace: PropTypes.string,
  lastSaleDate: PropTypes.string,
  isFeatureAvailableForOrg: PropTypes.bool,
  isGradientVisible: PropTypes.bool,
  lastSoldPrice: PropTypes.number,
  lastSoldCost: PropTypes.number,
  isPanelOpen: PropTypes.bool,
  cotaAtFloorPercentage: PropTypes.number,
};

export default SalesStreamContentPanel;
